import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import moment from 'moment';
import { combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { ReportIssueModel } from '../../components/report-issue/report-issue.model';
import { ConfigConstants } from '../../config/config-constants';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    SharpServiceNames,
} from '../../interfaces/client-manager.interface';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { NumberUtilities } from '../../utilities/number-utilities';
import { UserMasterService } from '../user-master/user-master.service';
import {
    Attachment,
    IncidentPriority,
    IncidentType,
    SharpProviderIncidentPayload,
    SharpProviderIncidentResponse,
    SharpProviderResponse,
    UserInfo,
} from '../../types/sharp-incidents';
import { FileUploadUtilities } from '../../utilities/file-upload-utilities';
import { safeStringify } from '@evorait/pace-components';
import { EnvironmentService } from '@evorait/pace-environment';
import { truncateStringStart } from '../../utilities/string.utilities';
import { DeviceLockFingerprint, LockState } from '../device/device.utils';
import { TranslateService } from '@ngx-translate/core';
import { ENDPOINTS } from '../../enums/endpoints.enum';

@Injectable({
    providedIn: 'root',
})
export class ReportIssueService {
    private readonly MAX_LOG_LINES = 5_000;
    private readonly MAX_LOG_LENGTH = 1_000_000;
    private readonly sharpServiceText: SharpServiceNames;

    public constructor(
        private readonly httpClient: HttpClient,
        private readonly userMasterService: UserMasterService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly environmentService: EnvironmentService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly paceInsights: PaceInsightsService,
        private readonly translate: TranslateService,
    ) {
        this.sharpServiceText = this.userMasterService.defaultSharpServiceText;
        if (this.userMasterService.isInitialized) {
            this.sharpServiceText = this.userMasterService.getSharpServiceText();
        }
    }

    public logForceDeviceUnlock(currentLock: LockState, thisFingerprint: DeviceLockFingerprint): Observable<unknown> {
        const options = {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        };
        const user = this.userMasterService.getSelectedUser();
        const requester = new UserInfo(user.frontId, user.emailId);
        const description = `User force-unlocked his device after acknowledging the following warning:

${safeStringify(this.translate.instant('DEVICE_LOCK.RESET_WARNING'))}

Current lock: ${safeStringify(currentLock)}

This fingerprint: ${safeStringify(thisFingerprint)}`;
        const body = new SharpProviderIncidentPayload(
            this.createExternalId(user.frontId),
            this.sharpServiceText,
            IncidentPriority.NOT_BLOCKED,
            'FORCE DEVICE UNLOCK EXECUTED',
            description,
            IncidentType.REQUEST,
            requester,
            requester,
            [],
            'Closed',
        );
        console.error('logForceDeviceUnlock');
        return this.sendPostRequest(`${this.environmentService.apiBase}${ENDPOINTS.SharpTicketUri}`, body, options);
    }

    public createSharpIncident(reportIssueModel: ReportIssueModel): Observable<void> {
        const HTTP_OK = 200;

        return combineLatest([this.getLogs(), this.getFileAttachments(reportIssueModel.files ?? [])]).pipe(
            take(1),
            mergeMap(([consoleLogAttachment, fileAttachments]) => {
                const allAttachments = [consoleLogAttachment, ...fileAttachments];
                const body = this.createPayload(reportIssueModel, allAttachments);

                const options = {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                };

                return this.sendPostRequest(
                    `${this.environmentService.apiBase}${ENDPOINTS.SharpTicketUri}`,
                    body,
                    options,
                );
            }),
            map((response: SharpProviderIncidentResponse[]) => {
                if (
                    response.length === 0 ||
                    (response && response.length && response[0].response?.statusLine?.statusCode !== HTTP_OK)
                ) {
                    throw new Error(`Sharp incident creation failed with error: ${safeStringify(response)}`);
                }
                this.log.info(`ReportIssueService: createSharpIncident: SHARP ticket successfully created.`);
            }),
            catchError((error) => {
                this.log.error(
                    `ReportIssueService: createSharpIncident: Error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return throwError(() => error);
            }),
        );
    }

    private getLogs(): Observable<Attachment> {
        return this.log.getLogs(this.MAX_LOG_LINES).pipe(
            map((logEntries) => {
                logEntries = truncateStringStart(logEntries, this.MAX_LOG_LENGTH);
                const payload = EncodingUtilities.base64EncodeUnicode(logEntries);
                return new Attachment(payload, 'console.log');
            }),
        );
    }

    private getFileAttachments(files: File[]): Observable<Attachment[]> {
        if (files.length === 0) {
            return of([]);
        }

        const attachmentObservables = files.map((file) => {
            return FileUploadUtilities.convertFileToBase64(file).pipe(
                map((payload: string) => {
                    const base64Data = FileUploadUtilities.removeBase64Prefix(payload);

                    return new Attachment(base64Data, file.name, file.type);
                }),
            );
        });

        return forkJoin(attachmentObservables).pipe(
            map((attachments) => {
                return attachments;
            }),
        );
    }

    private createExternalId(gid: string): string {
        return `${gid}-${this.sharpServiceText}-${moment.now()}-${NumberUtilities.getRandomNumberBetweenMinAndMax()}`;
    }

    private installationMode(): string {
        if (this.clientManager.isPacePWAStandalone()) {
            return 'Standalone';
        } else if (this.clientManager.isPacePWABrowser()) {
            return 'Browser';
        } else {
            return 'unknown mode';
        }
    }

    private getDetailedDescription(reportIssueModel: ReportIssueModel): string {
        return `
            PACE App version: tag ${this.clientManager.version.tag}, built ${this.clientManager.version.timestamp} based on commit ${this.clientManager.version.commitHash} in branch ${this.clientManager.version.branchName}

            Installation: [${this.installationMode()}] ${navigator.userAgent}

            Notification number: ${reportIssueModel.notification_num.value}

            Material number: ${reportIssueModel.material_num.value}

            Form ID: ${reportIssueModel.form_id.value}

            Date and time of issue: ${reportIssueModel.date_time.value.format(
                ConfigConstants.formatPatternDateTimeGeneric,
            )}

            Error Message: ${reportIssueModel.error_message.value}

            Actual Behavior: ${reportIssueModel.actual_behaviour.value}

            Expected Behavior: ${reportIssueModel.expected_behaviour.value}

            Exact steps to reproduce the incident: ${reportIssueModel.steps_to_reproduce.value}

            Personal Urgency: ${reportIssueModel.personal_urgency.selectedEntry.name}

            Tracing Session: ${this.paceInsights.getClientSession()}

            Support Id: ${this.paceInsights.getTraceId()}
        `;
    }

    private createPayload(reportIssueModel: ReportIssueModel, attachments: Attachment[]): SharpProviderIncidentPayload {
        const externalId = this.createExternalId(reportIssueModel.getGID());
        const priority = <IncidentPriority>reportIssueModel.personal_urgency.selectedEntry.value;
        let shortDescription = reportIssueModel.issue_description.value;
        const detailedDescription = this.getDetailedDescription(reportIssueModel);
        const incidentType = <IncidentType>reportIssueModel.category.selectedEntry.value;
        const requester = new UserInfo(reportIssueModel.getGID(), reportIssueModel.email);
        // Add an exclamation mark before the shortDescription only when Productivity is totally blocked
        if (priority === IncidentPriority.TOTALLY_BLOCKED) {
            shortDescription = '!' + shortDescription;
        }
        return new SharpProviderIncidentPayload(
            externalId,
            this.sharpServiceText,
            priority,
            shortDescription,
            detailedDescription,
            incidentType,
            requester,
            requester,
            attachments,
        );
    }

    public getEmailBody(reportIssueModel: ReportIssueModel): string {
        return `
        Hi,
        Please fill in the details below,

        SHARP_Caller: ${reportIssueModel.getGID()}
        SHARP_Service: ${this.sharpServiceText}
        SHARP_Category: ${reportIssueModel.category.selectedEntry.name}
        SHARP_Short_Description: ${reportIssueModel.issue_description.value}

        SHARP_Description:
            ${this.getDetailedDescription(reportIssueModel)}

        App log:`;
    }

    private sendPostRequest(
        url: string,
        body: string | SharpProviderIncidentPayload,
        options: {
            headers?:
                | HttpHeaders
                | {
                      [header: string]: string | string[];
                  };
        },
    ): Observable<SharpProviderResponse> {
        return this.httpClient.post<SharpProviderResponse>(url, body, options);
    }
}
